// HTTP client for the Lens API `vacancies` domain.
//
// Mirrors the shape of src/lib/fs/vacancies.ts (same function names, same
// parameter order) so consumers can swap `../fs/vacancies` for `../api/vacancies`
// with minimal ceremony. Legacy parameters like `repoPath` are accepted but
// ignored — Postgres is the store now, not the filesystem.
//
// The API row shape (core.vacancies) is intentionally sparser than the
// frontend Vacancy model. This module maps: the frontend's rich fields
// live inside the row's `extracted` JSON column and are flattened on read.

import { acquireLensApiToken } from "../auth/msal";
import type { Vacancy, VacancyRequirements, VacancyRoute } from "../fs/vacancies";

// Row shape returned by GET /api/vacancies (matches core.vacancies)
interface VacancyRow {
  id: string;
  title: string;
  client: string | null;
  location: string | null;
  source_url: string | null;
  status: string;
  extracted: {
    added_at?: string;
    added_by_oid?: string;
    hours_per_week?: number;
    remote_policy?: "remote" | "hybrid" | "onsite";
    start_date?: string;
    duration_months?: number;
    zzp_allowed?: boolean;
    dutch_language_requirement?: string;
    requirements?: VacancyRequirements;
    full_text?: string;
  } | null;
  routes: VacancyRoute[] | null;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

// ---------------------------------------------------------------------------

async function api<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = await acquireLensApiToken();
  const res = await fetch(path, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let detail: string;
    try {
      const j = await res.json();
      detail = j.message ?? JSON.stringify(j);
    } catch {
      detail = await res.text();
    }
    throw new Error(`${method} ${path} → ${res.status}: ${detail}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// ---------------------------------------------------------------------------
// Row ↔ frontend shape mapping

function rowToVacancy(row: VacancyRow): Vacancy {
  const ex = row.extracted ?? {};
  return {
    id: row.id,
    title: row.title,
    client: row.client ?? "",
    added_at: ex.added_at ?? row.created_at,
    added_by_oid: ex.added_by_oid ?? row.created_by,
    hours_per_week: ex.hours_per_week ?? 0,
    location: row.location ?? "",
    remote_policy: ex.remote_policy ?? "onsite",
    start_date: ex.start_date ?? "",
    duration_months: ex.duration_months ?? 0,
    zzp_allowed: ex.zzp_allowed ?? false,
    dutch_language_requirement: ex.dutch_language_requirement ?? "",
    requirements: ex.requirements ?? { hard: [], preferred: [], competencies: [] },
    routes: row.routes ?? [],
    full_text: ex.full_text,
  };
}

function vacancyToWrite(v: Omit<Vacancy, "id" | "routes">): Partial<VacancyRow> {
  return {
    title: v.title,
    client: v.client,
    location: v.location,
    source_url: null,
    status: "open",
    extracted: {
      added_at: v.added_at,
      added_by_oid: v.added_by_oid,
      hours_per_week: v.hours_per_week,
      remote_policy: v.remote_policy,
      start_date: v.start_date,
      duration_months: v.duration_months,
      zzp_allowed: v.zzp_allowed,
      dutch_language_requirement: v.dutch_language_requirement,
      requirements: v.requirements,
      full_text: v.full_text,
    },
  };
}

// ---------------------------------------------------------------------------
// Public API — mirrors src/lib/fs/vacancies.ts

export async function readAllVacancies(_repoPath?: string): Promise<Vacancy[]> {
  const rows = await api<VacancyRow[]>("GET", "/api/vacancies");
  return rows.filter((r) => !r.deleted_at).map(rowToVacancy);
}

export async function readVacancy(_repoPath: string, vacancyId: string): Promise<Vacancy | null> {
  try {
    const row = await api<VacancyRow>("GET", `/api/vacancies/${encodeURIComponent(vacancyId)}`);
    return rowToVacancy(row);
  } catch (err) {
    console.error("readVacancy failed", err);
    return null;
  }
}

export async function saveNewVacancy(
  _repoPath: string,
  vacancyData: Omit<Vacancy, "id" | "routes">,
  routeData: Omit<VacancyRoute, "id" | "seed_path">,
  _screenshotBase64: string,
): Promise<Vacancy> {
  const routeId = "route_001";
  const routeWithId: VacancyRoute = {
    ...routeData,
    id: routeId,
    seed_path: "", // blob storage integration is a later session
  };
  const payload = {
    ...vacancyToWrite(vacancyData),
    routes: [routeWithId],
  };
  const row = await api<VacancyRow>("POST", "/api/vacancies", payload);
  return rowToVacancy(row);
}

export async function appendVacancyRoute(
  _repoPath: string,
  vacancyId: string,
  routeData: Omit<VacancyRoute, "id" | "seed_path">,
  _screenshotBase64: string,
): Promise<Vacancy> {
  const current = await api<VacancyRow>("GET", `/api/vacancies/${vacancyId}`);
  const existing = current.routes ?? [];
  const routeIndex = existing.length + 1;
  const routeId = `route_${String(routeIndex).padStart(3, "0")}`;
  const newRoute: VacancyRoute = { ...routeData, id: routeId, seed_path: "" };
  const updated = await api<VacancyRow>("PUT", `/api/vacancies/${vacancyId}`, {
    routes: [...existing, newRoute],
  });
  return rowToVacancy(updated);
}

export async function deleteVacancy(_repoPath: string, vacancyId: string): Promise<void> {
  await api<void>("DELETE", `/api/vacancies/${vacancyId}`);
}

// Re-export types so callers importing from api/vacancies get the same
// interface surface as they would from fs/vacancies.
export type { Vacancy, VacancyRoute, VacancyRequirements };
